import {
  DragDrop,
  Droppable,
  Draggable,
  DualListSelector,
  DualListSelectorPane,
  DualListSelectorList,
  DualListSelectorListItem,
  DualListSelectorControlsWrapper,
  DualListSelectorControl,
} from '@patternfly/react-core'
import {
  AngleDoubleLeftIcon,
  AngleDoubleRightIcon,
  AngleLeftIcon,
  AngleRightIcon,
} from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useState } from 'react'

import type { IInstrument } from '@app/domain/instrument/instrument.interface'


interface ControlsProps {
  canMoveAllFromAvailable: boolean
  moveAllToAvailableIsDisabled: boolean
  moveSelectedFromAvailableIsDisabled: boolean
  moveSelectedToAvailableIsDisabled: boolean
  onMoveAllFromAvailable: () => void
  onMoveAllToAvailable: () => void
  onMoveSelectedFromAvailable: () => void
  onMoveSelectedToAvailable: () => void
}

const InstrumentSelectorControls = ({
  canMoveAllFromAvailable,
  moveAllToAvailableIsDisabled,
  moveSelectedFromAvailableIsDisabled,
  moveSelectedToAvailableIsDisabled,
  onMoveAllFromAvailable,
  onMoveAllToAvailable,
  onMoveSelectedFromAvailable,
  onMoveSelectedToAvailable,
}: ControlsProps) => {
  return (
    <React.Fragment>
      <DualListSelectorControlsWrapper aria-label="Selector controls">
        <DualListSelectorControl
          isDisabled={moveSelectedFromAvailableIsDisabled}
          onClick={onMoveSelectedFromAvailable}
          aria-label="Add selected"
        >
          <AngleRightIcon />
        </DualListSelectorControl>
        <DualListSelectorControl
          isDisabled={canMoveAllFromAvailable}
          onClick={onMoveAllFromAvailable}
          aria-label="Add all"
        >
          <AngleDoubleRightIcon />
        </DualListSelectorControl>
        <DualListSelectorControl
          isDisabled={moveAllToAvailableIsDisabled}
          onClick={onMoveAllToAvailable}
          aria-label="Remove all"
        >
          <AngleDoubleLeftIcon />
        </DualListSelectorControl>
        <DualListSelectorControl
          onClick={onMoveSelectedToAvailable}
          isDisabled={moveSelectedToAvailableIsDisabled}
          aria-label="Remove selected"
        >
          <AngleLeftIcon />
        </DualListSelectorControl>
      </DualListSelectorControlsWrapper>
    </React.Fragment>
  )
}

interface Props {
  allInstruments: IInstrument[]
  selectedInstruments: IInstrument[]
  onSelectedUpdated: (instruments: IInstrument[]) => void
}

interface IInstrumentOptions {
  text: string
  id: string
  selected: boolean
  isVisible: boolean
}

function instrumentToOptions(instrument: IInstrument): IInstrumentOptions {
  const text = instrument.label ? instrument.label : instrument.id
  const id = instrument.id

  return { text, id, selected: false, isVisible: true }
}

function getAvailableInstruments(
  allInstruments: IInstrument[],
  selectedInstruments: IInstrument[],
): IInstrument[] {
  const choosenInstruments = new Set(selectedInstruments?.map((i) => i.id))
  return allInstruments?.filter((i) => !choosenInstruments.has(i.id))
}

export const InstrumentSelector = ({
  allInstruments,
  onSelectedUpdated,
  selectedInstruments,
}: Props) => {
  const [ignoreNextOptionSelect, setIgnoreNextOptionSelect] =
    React.useState(false)
  const [availableOptions, setAvailableOptions] = useState<
    IInstrumentOptions[]
  >([])
  const [selectedOptions, setSelectedOptions] = useState<IInstrumentOptions[]>(
    [],
  )

  React.useEffect(() => {
    const available = getAvailableInstruments(
      allInstruments,
      selectedInstruments,
    ).map(instrumentToOptions)
    const selected = selectedInstruments.map(instrumentToOptions)
    setAvailableOptions(available)
    setSelectedOptions(selected)
  }, [allInstruments, selectedInstruments])

  const optionToInstrument = useCallback(
    (option: IInstrumentOptions) => {
      return allInstruments?.filter((i) => i.id === option.id)[0]
    },
    [allInstruments],
  )

  const handleMoveSelectedFromAvailable = useCallback(() => {
    const sourceOptions = availableOptions
    const destinationOptions = selectedOptions
    for (let i = 0; i < sourceOptions.length; i++) {
      const option = sourceOptions[i]
      if (option.selected && option.isVisible) {
        sourceOptions.splice(i, 1)
        destinationOptions.push(option)
        option.selected = false
        i--
      }
    }
    onSelectedUpdated([...destinationOptions].map(optionToInstrument))
  }, [availableOptions, selectedOptions, optionToInstrument, onSelectedUpdated])

  const handleMoveSelectedToAvailable = useCallback(() => {
    const sourceOptions = selectedOptions
    const destinationOptions = availableOptions
    for (let i = 0; i < sourceOptions.length; i++) {
      const option = sourceOptions[i]
      if (option.selected && option.isVisible) {
        sourceOptions.splice(i, 1)
        destinationOptions.push(option)
        option.selected = false
        i--
      }
    }
    onSelectedUpdated([...sourceOptions].map(optionToInstrument))
  }, [availableOptions, selectedOptions, optionToInstrument, onSelectedUpdated])

  const handleMoveAllFromAvailable = useCallback(() => {
    onSelectedUpdated(allInstruments)
  }, [allInstruments, onSelectedUpdated])

  const handleMoveAllToAvailable = useCallback(() => {
    onSelectedUpdated([])
  }, [onSelectedUpdated])

  const onOptionSelect = useCallback(
    (_event, index, isSelected) => {
      if (ignoreNextOptionSelect) {
        setIgnoreNextOptionSelect(false)
        return
      }
      if (isSelected) {
        const newSelected = [...selectedOptions]
        newSelected[index].selected = !selectedOptions[index].selected
        setSelectedOptions(newSelected)
      } else {
        const newAvailable = [...availableOptions]
        newAvailable[index].selected = !availableOptions[index].selected
        setAvailableOptions(newAvailable)
      }
    },
    [availableOptions, selectedOptions, ignoreNextOptionSelect],
  )

  const handleDrag = useCallback(() => {
    setIgnoreNextOptionSelect(true)
    return true
  }, [])

  const handleDrop = useCallback(
    (source, dest) => {
      if (dest) {
        const newList = [...selectedOptions]
        const [removed] = newList.splice(source.index, 1)
        newList.splice(dest.index, 0, removed)
        onSelectedUpdated(newList.map(optionToInstrument))
        return true
      }
      return false
    },
    [selectedOptions, optionToInstrument, onSelectedUpdated],
  )

  return (
    <DualListSelector>
      <DualListSelectorPane
        title="Available Instruments"
        status={`${
          availableOptions.filter((x) => x.selected && x.isVisible).length
        } of ${
          availableOptions.filter((x) => x.isVisible).length
        } options selected`}
      >
        <DualListSelectorList>
          {availableOptions.map((option, index) => {
            return option.isVisible ? (
              <DualListSelectorListItem
                key={index}
                isSelected={option.selected}
                id={`composable-available-option-${index}`}
                onOptionSelect={(e) => onOptionSelect(e, index, false)}
              >
                {option.text}
              </DualListSelectorListItem>
            ) : null
          })}
        </DualListSelectorList>
      </DualListSelectorPane>
      <InstrumentSelectorControls
        canMoveAllFromAvailable={availableOptions.length === 0}
        moveAllToAvailableIsDisabled={selectedOptions.length === 0}
        moveSelectedFromAvailableIsDisabled={
          !availableOptions.some((option) => option.selected)
        }
        moveSelectedToAvailableIsDisabled={
          !selectedOptions.some((option) => option.selected)
        }
        onMoveAllFromAvailable={handleMoveAllFromAvailable}
        onMoveAllToAvailable={handleMoveAllToAvailable}
        onMoveSelectedFromAvailable={handleMoveSelectedFromAvailable}
        onMoveSelectedToAvailable={handleMoveSelectedToAvailable}
      />
      <DragDrop onDrag={handleDrag} onDrop={handleDrop}>
        <DualListSelectorPane
          title="Selected Instruments"
          status={`${
            selectedOptions.filter((x) => x.selected && x.isVisible).length
          } of ${
            selectedOptions.filter((x) => x.isVisible).length
          } options selected`}
          isChosen
        >
          <Droppable hasNoWrapper>
            <DualListSelectorList>
              {selectedOptions.map((option, index) => {
                return option.isVisible ? (
                  <Draggable key={index} hasNoWrapper>
                    <DualListSelectorListItem
                      isSelected={option.selected}
                      id={`composable-selected-option-${index}`}
                      onOptionSelect={(e) => onOptionSelect(e, index, true)}
                      isDraggable
                    >
                      {option.text}
                    </DualListSelectorListItem>
                  </Draggable>
                ) : null
              })}
            </DualListSelectorList>
          </Droppable>
        </DualListSelectorPane>
      </DragDrop>
    </DualListSelector>
  )
}
